import React, { useState, useEffect, useRef } from 'react';
import {
    FormControl,
    Select,
    MenuItem,
    Typography,
    Box,
    CircularProgress,
    Chip
} from '@mui/material';
import { FaHistory } from 'react-icons/fa';
import AiAgentService from '../../../api/AiAgentService';
import useFormatDate from '../../../hooks/useFormatDate';

const VersionSelector = ({ promptId, currentVersion, onVersionChange, selectedVersion }) => {
    const { formatDateTime } = useFormatDate();
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedVersionId, setSelectedVersionId] = useState(selectedVersion?.id || currentVersion?.id || '');
    const menuRef = useRef(null);

    const PAGE_SIZE = 5;

    // Завантаження версій
    const loadVersions = async (pageNum = 1, append = false) => {
        try {
            setLoading(true);
            
            const params = {
                versions_page: pageNum,
                versions_page_size: PAGE_SIZE
            };
            
            const response = await AiAgentService.getPromptWithVersions(params);
            
            if (response?.versions) {
                if (append) {
                    setVersions(prev => [...prev, ...response.versions]);
                } else {
                    setVersions(response.versions);
                }
                
                setHasMore(pageNum < (response.versions_total_pages || 1));
            }
        } catch (err) {
            console.error('Помилка завантаження версій:', err);
        } finally {
            setLoading(false);
        }
    };

    // Завантаження перших версій при монтуванні
    useEffect(() => {
        if (promptId) {
            loadVersions(1);
        }
    }, [promptId]);

    // Оновлення вибраної версії при зміні currentVersion
    useEffect(() => {
        const data = selectedVersion?.id || currentVersion?.id;
        if (data) {
            setSelectedVersionId(data);
        }
    }, [currentVersion, selectedVersion]);

    // Обробка прокручування меню
    const handleScroll = (event) => {
        const element = event.target;
        const scrolledToBottom = 
            element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

        if (scrolledToBottom && hasMore && !loading) {
            const nextPage = page + 1;
            setPage(nextPage);
            loadVersions(nextPage, true);
        }
    };

    // Обробка зміни версії
    const handleChange = (event) => {
        const versionId = event.target.value;
        setSelectedVersionId(versionId);
        
        const selectedVersion = versions.find(v => v.id === versionId);
        if (selectedVersion && onVersionChange) {
            onVersionChange(selectedVersion);
        }
    };

    return (
        <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaHistory /> Версія промпту
            </Typography>
            <Select
                value={selectedVersionId}
                onChange={handleChange}
                displayEmpty
                MenuProps={{
                    PaperProps: {
                        ref: menuRef,
                        onScroll: handleScroll,
                        style: {
                            maxHeight: 400,
                        },
                    },
                }}
            >
                {versions.length === 0 && !loading && (
                    <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                            Немає доступних версій
                        </Typography>
                    </MenuItem>
                )}
                
                {versions.map((version) => (
                    <MenuItem 
                        key={version.id} 
                        value={version.id}
                        sx={{ 
                            flexDirection: 'column', 
                            alignItems: 'flex-start',
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    fontFamily: 'monospace',
                                    fontWeight: version.id === currentVersion?.id ? 'bold' : 'normal'
                                }}
                            >
                                {version.commit_hash?.substring(0, 8)}
                            </Typography>
                            {version.id === currentVersion?.id && (
                                <Chip 
                                    label="Поточна" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ height: 20 }}
                                />
                            )}
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                            {version.description || 'Без опису'}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                            <Typography variant="caption" color="text.secondary">
                                Автор: {version.author}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatDateTime(version.created_at)}
                            </Typography>
                        </Box>
                        
                        {version.tags && (
                            <Box sx={{ mt: 0.5 }}>
                                {version.tags.split(',').map((tag, idx) => (
                                    <Chip 
                                        key={idx} 
                                        label={tag.trim()} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ mr: 0.5, height: 18, fontSize: '0.7rem' }}
                                    />
                                ))}
                            </Box>
                        )}
                    </MenuItem>
                ))}
                
                {loading && (
                    <MenuItem disabled>
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 1 }}>
                            <CircularProgress size={20} />
                        </Box>
                    </MenuItem>
                )}
                
                {hasMore && !loading && (
                    <MenuItem disabled>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Прокрутіть вниз для завантаження більше...
                        </Typography>
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    );
};

export default VersionSelector;
